/**
 * @fileOverview
 * @author Donny
 */

// lib
import '@ali/lib-mtop';
import { MTOP_DEBOUNCE_TIME, REAL_MTOP_ARRAY } from "../constants/config";

/**
 * 检查mtop返回值
 * @return {Boolen}               [返回值是否可用]
 */
const validateMtopResponse = response => response.code * 1 === 0;

/**
 * 线上域名白名单
 */
const DOMAIN_WHITE_LIST = ['alibaba-inc.com'];


/*
* fetchCache 防止鼠标连续点击而重复发送消息
* */
const fetchCache = (() => {
  const cache = {};
  return {
    /**
     * 返回是否拦截
     * @param api
     * @param param
     * @returns {boolean|(boolean|void)}
     */
    check(api, param) {
      return !!cache[api] && (function() {
        const nowTime = +new Date();
        cache[api].preTime = cache[api].preTime || 0;
        if (cache[api].preTime + MTOP_DEBOUNCE_TIME > nowTime) {
          return true;
        }
      }());
    },
    get(api) {
      // 返回一个Promise
      return cache[api].promise;
    },
    insert(api, data, promise) {
      // 接口放入cache中
      cache[api] = {
        param: data,
        promise,
        preTime: +new Date(),// 用于记录上次的请求时间
      };
    },
    /**
     * 从cach中移除api接口
     * @param api：请求的接口
     * @param checkMove：是否进行校验移除
     */
    remove(api, checkMove) {
      // 如果需要进行校验（一般就是用于请求成功，接口返回时间很小）
      if (checkMove && cache[api]) {
        const nowTime = +new Date();
        cache[api].preTime = cache[api].preTime || 0;
        // 上次请求时间 + 最小时间间隔 < 当前时间，说明下次可以进行请求
        if (cache[api].preTime + MTOP_DEBOUNCE_TIME < nowTime) {
          delete cache[api];
        }
      } else {
        delete cache[api];
      }
    }
  };
})();

/*
 * 通过mtop获取数据
 * @param  {String} token         [token]
 * @param  {Object} env           [Env对象]
 * @param  {String} prefix        [mtop前缀]
 * @param  {String} api           [mtop方法名]
 * @param  {Object} data          [mtop对象]
 * @param  {Object} opt           [mtop可选参数]
 * @return {Object}               [Promise对象]
 * */
export const mtopFetch = ({ token, env, prefix }) => {
  // param check
  if(!env) {
    throw new Error(`param error, env: ${env}`);
  }
  if(typeof env.isDevOrDaily !== "function" || typeof env.isProd !== "function" || typeof env.isPre !== "function") {
    throw new Error('env object error')
  }
  // prefix设置
  if (env.isProd()) {
    lib.mtop.config.prefix = 'h5api';
    lib.mtop.config.subDomain = 'm';
    const mainDomain = location.hostname.split('.').slice(-2).join('.');
    lib.mtop.config.mainDomain = DOMAIN_WHITE_LIST.some(domain => domain === mainDomain) ? 'taobao.com' : mainDomain;
  } else if (env.isPre()) {
    lib.mtop.config.prefix = 'h5api';
    lib.mtop.config.subDomain = 'wapa';
    lib.mtop.config.mainDomain = 'taobao.com';
  } else {
    lib.mtop.config.subDomain = 'waptest';
    lib.mtop.config.mainDomain = 'taobao.com';
  }
  // 拦截器
  let _injection = {};
  return {
    request(api, data, { v = '1.0', ecode = 0, timeout = 20000 } = {}) {
      for (const i in data) {
        if (typeof data[i] === 'object' && data.hasOwnProperty(i)) {
          data[i] = JSON.stringify(data[i]);
        }
      }
      // 检查fetchCache, 防止鼠标连点而多次发送消息
      if (fetchCache.check(api, data) && !REAL_MTOP_ARRAY.find(apiItem => apiItem === api)) return fetchCache.get(api);

      const p = new Promise((resolve, reject) => {
        let token, ext = {};
        if (_injection.token && typeof _injection.token === 'function') {
          token = _injection.token();
        } else if (_injection.token && typeof _injection.token === 'string') {
          token = _injection.token;
        }
        // 添加额外必传参数
        if (_injection.ext && typeof _injection.ext === 'object') {
          ext = Object.assign({}, _injection.ext);
        }
        lib.mtop.request({
          api: `${prefix ? prefix + '.' : ''}${api}`,
          v,
          data: { token, ...data, ...ext },
          ecode,
          type: env.isDevOrDaily() ? 'GET' : 'POST',
          dataType: env.isDevOrDaily() ? 'originaljsonp' : 'json',
          timeout,
          tb_eagleeyex_scm_project: api === 'register.stepone.submit' ? null : _injection.tb_eagleeyex_scm_project // todo 隔离环境，以后会删除
        }).then((response) => {
          // 消息返回，从cache中移除
          fetchCache.remove(api, true);

          if (validateMtopResponse(response.data)) {
            // intercept if needed
            if (_injection.success) {
              _injection.success(response.data.value);
            }
            return resolve(response.data.value);
          }
          const res = {
            code: response.data && response.data.code,
            msg: response.data && response.data.message && response.data.message.cust,
            dev: response.data && response.data.message && response.data.message.dev,
          };
          // intercept if needed
          if (_injection.failure) {
            _injection.failure(res);
          }
          return reject(res)
        }, (error) => {
          // 消息返回，从cache中移除
          fetchCache.remove(api);
          const res = {
            code: -1,
            msg: error && error.ret && error.ret[0].split('::')[1],
          };
          // intercept if needed
          if (_injection.failure) {
            _injection.failure(res);
          }
          return reject(res)
        });
      });

      // 将消息放入cache
      fetchCache.insert(api, data, p);

      return p;
    },
    inject(injection) {
      Object.assign(_injection, injection);
    }
  }
};

export default mtopFetch;


// WEBPACK FOOTER //
// ./src/utils/mtopFetch.js